import React, { useState, useEffect } from "react";
import {
  Box,
  useColorMode,
  useToast,
  Flex,
  Heading,
  Text,
} from "@chakra-ui/react";
import LotteryNumberDisplay from "./components/LotteryNumberDisplay";
import FloatingFAQ from "./components/FloatingFAQ";
import TopNav from "./components/TopNav";
import MobileNav from "./components/MobileNav";
import { apiFetch } from "./utils/api";
import { useAccount } from "wagmi";
import { logFrontendError } from "./utils/logger";
import { Helmet } from "react-helmet";

function MaintenanceCountdown({ untilIso }) {
  const [remaining, setRemaining] = React.useState("");
  const [ended, setEnded] = React.useState(false);

  React.useEffect(() => {
    const parseUntil = () => {
      const raw =
        untilIso ||
        process.env.REACT_APP_MAINTENANCE_UNTIL ||
        process.env.REACT_APP_MAINTENANCE_TIMESTAMP ||
        process.env.REACT_APP_MAINTENANCE_TIMPESTAMP;

      const t = raw ? new Date(raw) : null;
      return t && !isNaN(t.getTime()) ? t : null;
    };

    const target = parseUntil();

    const tick = () => {
      if (!target) {
        setRemaining("—");
        setEnded(false);
        return;
      }

      const now = new Date();
      const diff = target.getTime() - now.getTime();

      if (diff <= 0) {
        setRemaining("00:00:00");
        setEnded(true);

        if (typeof window.__lotoroOnMaintenanceEnd === "function") {
          Promise.resolve().then(() => window.__lotoroOnMaintenanceEnd());
        }

        return;
      }

      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      const pad = (n) => String(n).padStart(2, "0");

      setRemaining(`${pad(h)}:${pad(m)}:${pad(s)}`);
    };

    tick();

    const id = setInterval(tick, 1000);

    return () => clearInterval(id);
  }, [untilIso]);

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      minH="40vh"
      gap={4}
    >
      <Heading
        fontSize={{ base: "5xl", md: "6xl" }}
        letterSpacing="wider"
        textAlign="center"
      >
        Maintenance Mode
      </Heading>

      <Heading
        fontSize={{ base: "6xl", md: "7xl" }}
        letterSpacing="wider"
        color="green.400"
      >
        {remaining}
      </Heading>

      <Text fontSize="lg" maxW="720px" textAlign="center" opacity={0.85}>
        Lotoro is temporarily paused for scheduled maintenance. During this
        time, draws and ticket purchases are unavailable. We’ll be back as soon
        as the countdown reaches zero.
        <br />
        <Text as="span" color="green.400" fontWeight="semibold">
          Note: after maintenance, our daily reset will switch to 21:00 UTC
          (instead of 00:00 UTC).
        </Text>
      </Text>

      {ended && (
        <Text fontSize="md" color="red.400" fontWeight="semibold">
          If the countdown is at 00:00:00, please refresh the page.
        </Text>
      )}
    </Flex>
  );
}

export default function Lotoro() {
  const [isMobile, setIsMobile] = useState(false);
  const { address } = useAccount();
  const walletAddress = address;
  const [prizePool, setPrizePool] = useState(0.0);
  const [countdownToNoon, setCountdownToNoon] = useState("");
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { colorMode, toggleColorMode } = useColorMode();
  const toast = useToast();
  const [maintenanceOver, setMaintenanceOver] = useState(false);

  useEffect(() => {
    window.__lotoroOnMaintenanceEnd = () => {
      setMaintenanceOver(true);
      window.__lotoroOnMaintenanceEnd = null;
    };

    return () => {
      window.__lotoroOnMaintenanceEnd = null;
    };
  }, []);

  const toggleSearchBar = () => setShowSearchBar((prev) => !prev);

  const handleSearch = async (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setSearchTerm(value);

    if (value.length === 6) {
      try {
        const res = await fetch("/api/check-number", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ number: value }),
        });

        const result = await res.json();

        if (result?.isWinner) {
          toast({
            title: "🎉 WOW a winner number!",
            description: `The number ${value} matched Tier ${result.tier} (${result.tierLabel}) — Drawn number: ${result.matchedDrawNumber}`,
            status: "success",
            duration: 8000,
            isClosable: true,
            position: "top",
          });
        } else {
          toast({
            title: "No match found",
            description: `Number ${value} did not win in the current payout window.`,
            status: "info",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
        }
      } catch (err) {
        logFrontendError("Search check-number failed", err?.stack || "");

        toast({
          title: "Search failed",
          description: "Could not check the number. Try again later.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      }
    }
  };

  const isMaintenance =
    String(process.env.REACT_APP_MAINTENANCE || "").toLowerCase() === "true";

  const maintenanceUntil =
    process.env.REACT_APP_MAINTENANCE_UNTIL ||
    process.env.REACT_APP_MAINTENANCE_TIMESTAMP ||
    process.env.REACT_APP_MAINTENANCE_TIMPESTAMP;

  const showMaintenance = isMaintenance && !maintenanceOver;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchPrizePool() {
      try {
        const data = await apiFetch("/app/state");

        if (!cancelled) {
          setPrizePool(Number(data.prizePool || 0));
        }
      } catch (error) {
        logFrontendError(
          "Failed to fetch prize pool",
          error?.stack || error?.message || "",
        );
      }
    }

    fetchPrizePool();

    const intervalId = setInterval(fetchPrizePool, 10000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();

      const target = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          21,
          0,
          0,
          0,
        ),
      );

      if (now >= target) {
        target.setUTCDate(target.getUTCDate() + 1);
      }

      let diff = target - now;

      if (diff < 0) {
        diff = 0;
      }

      const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");

      setCountdownToNoon(`${h}:${m}:${s}`);
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box position="relative" minH="100vh" overflow="hidden">
      <Helmet>
        <title>Lotoro - Fair Crypto Lottery | Win Daily USDT</title>

        <meta
          name="description"
          content="Lotoro is a fair, transparent crypto lottery with real-time draws and daily USDT payouts. Play daily, win big!"
        />

        <meta
          name="keywords"
          content="crypto lottery, blockchain lottery, USDT prize, daily draw, fair lottery, web3 lottery"
        />

        <meta property="og:title" content="Lotoro - Fair Crypto Lottery" />

        <meta
          property="og:description"
          content="Join Lotoro for fair crypto draws. Win daily prizes with transparent payouts."
        />

        <meta
          property="og:image"
          content="https://lotoro.co/lotoro-og-image.png"
        />

        <meta property="og:url" content="https://lotoro.co" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="twitter:title" content="Lotoro - Crypto Lottery" />

        <meta
          name="twitter:description"
          content="Join Lotoro for fair, transparent crypto draws with daily USDT prizes."
        />

        <meta
          name="twitter:image"
          content="https://lotoro.co/lotoro-og-image.png"
        />

        <link rel="canonical" href="https://lotoro.co" />
      </Helmet>

      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg={
          colorMode === "light"
            ? "radial-gradient(circle at 50% 30%, #f5f5f5, #e0e0e0, #ffffff)"
            : "radial-gradient(circle at 50% 30%, #0D0D0D, #000000)"
        }
        zIndex="base"
      />

      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="rgba(255, 255, 255, 0.05)"
        backdropFilter="blur(40px)"
        zIndex="base"
      />

      <Box position="relative" zIndex="1" p={4}>
        <MobileNav
          colorMode={colorMode}
          toggleColorMode={toggleColorMode}
          toggleSearchBar={toggleSearchBar}
          showSearchBar={showSearchBar}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          countdownToNoon={countdownToNoon}
          prizePool={prizePool}
        />

        <TopNav
          colorMode={colorMode}
          toggleColorMode={toggleColorMode}
          prizePool={prizePool}
          countdownToNoon={countdownToNoon}
          showSearchBar={showSearchBar}
          toggleSearchBar={toggleSearchBar}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          isMobile={isMobile}
        />

        {showMaintenance ? (
          <Box position="relative" zIndex="1" p={4}>
            <MaintenanceCountdown untilIso={maintenanceUntil} />
          </Box>
        ) : (
          <Flex
            direction="column"
            minHeight="50vh"
            align="center"
            justify="center"
            gap={8}
            pb={{ base: 20, md: 10 }}
          >
            <LotteryNumberDisplay walletAddress={walletAddress} />
          </Flex>
        )}

        {!showMaintenance && <FloatingFAQ />}
      </Box>
    </Box>
  );
}
